/**
 * <facet-filters>: progressive enhancement for collection / search results.
 *  - Filter chips are links ([data-facet-link]); sort is a <select>.
 *    Both update results in place via the Section Rendering API and keep the
 *    URL in sync (back/forward supported).
 *  - "Load more" ([data-load-more]) appends the next page of products.
 * Only [data-facet-region] nodes are swapped, so an open filter drawer stays
 * open while filters change.
 */

const { fetchSection, announce } = window.Theme;

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => this.onChange(event));
    this.onPopState = () => this.render(window.location.href, false);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  onClick(event) {
    const link = event.target.closest('a[data-facet-link]');
    if (link) {
      event.preventDefault();
      this.render(link.href);
      return;
    }
    const more = event.target.closest('a[data-load-more]');
    if (more) {
      event.preventDefault();
      this.loadMore(more);
    }
  }

  onChange(event) {
    const select = event.target.closest('select[data-facet-sort]');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    this.render(url.toString());
  }

  async render(url, push = true) {
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-facet-region="results"]')?.classList.add('opacity-50', 'transition-opacity');

    try {
      const doc = await fetchSection(url, this.sectionId);
      this.querySelectorAll('[data-facet-region]').forEach((region) => {
        const next = doc.querySelector(`[data-facet-region="${region.dataset.facetRegion}"]`);
        if (next) region.replaceWith(next);
      });

      const inline = doc.querySelector('[data-facet-count-inline]')?.textContent ?? '';
      this.querySelectorAll('[data-facet-count-inline]').forEach((el) => (el.textContent = inline));
      const count = doc.querySelector('[data-facet-count]')?.textContent.trim();
      if (count) announce(count);

      if (push) window.history.pushState({}, '', url);
    } catch {
      window.location.href = url;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  async loadMore(button) {
    button.setAttribute('aria-busy', 'true');
    button.classList.add('opacity-50', 'pointer-events-none');
    try {
      const doc = await fetchSection(button.href, this.sectionId);
      const items = [...doc.querySelectorAll('[data-facet-grid] > [data-product-item]')];
      const firstNew = items[0];
      this.querySelector('[data-facet-grid]')?.append(...items);
      const pagination = doc.querySelector('[data-facet-pagination]');
      if (pagination) this.querySelector('[data-facet-pagination]')?.replaceWith(pagination);
      if (button.isConnected) button.remove();
      firstNew?.querySelector('a')?.focus({ preventScroll: true });
      announce(pagination?.querySelector('p')?.textContent.trim() || '');
    } catch {
      window.location.href = button.href;
    }
  }
}

if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
